import { useEffect, useRef, useState } from 'react';
import './Ledger.css';
import { SortHead } from './SortHead';

export function StatusDotPill({ status = 'info', label }) {
  return (
    <span className={`status-dot-pill s-${status}`}>
      <span className="dot" aria-hidden="true" />
      {label}
    </span>
  );
}

export function Plazo({ dias, label, urgency = 'loose' }) {
  return (
    <span className={`plazo ${urgency}`}>
      <span className="dias">{dias}</span>
      {label && <span className="lbl">{label}</span>}
    </span>
  );
}

export function Ledger({
  columns = [],
  rows = [],
  onRowClick,
  activeId,
  density = 'comfortable',
  sortKey,
  sortBy = 'asc',
  onSortChange,
}) {
  // Column-header sort: a column with a `sortKey` becomes a clickable SortHead.
  // Clicking the active column toggles direction; a new column starts ascending.
  // The parent owns the server query, so we just emit (key, dir).
  const handleHeadSort = (key) =>
    onSortChange &&
    onSortChange(key, key === sortKey && sortBy === 'asc' ? 'desc' : 'asc');
  // Vim-style row navigation: J/K move a row cursor (the first J/K focuses the
  // table by landing on row 0), Enter opens the cursored row. Shared here so
  // every Ledger-based table inherits it. Ignored while typing in a field.
  const [cursor, setCursor] = useState(-1);
  const tbodyRef = useRef(null);

  // Keep the cursor in range as rows change (filter / sort / infinite scroll).
  useEffect(() => {
    setCursor((c) => (c >= rows.length ? rows.length - 1 : c));
  }, [rows.length]);

  useEffect(() => {
    function onKey(e) {
      const t = e.target;
      const tag = t?.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        t?.isContentEditable ||
        e.metaKey ||
        e.ctrlKey ||
        e.altKey
      ) {
        return;
      }
      const k = e.key.toLowerCase();
      if (k === 'j') {
        e.preventDefault();
        setCursor((c) => (c < 0 ? 0 : Math.min(rows.length - 1, c + 1)));
      } else if (k === 'k') {
        e.preventDefault();
        setCursor((c) => (c < 0 ? 0 : Math.max(0, c - 1)));
      } else if (e.key === 'Enter') {
        // Let a focused control handle its own Enter.
        if (tag === 'BUTTON' || tag === 'A') return;
        const row = rows[cursor];
        if (row) {
          e.preventDefault();
          row.onClick ? row.onClick(row) : onRowClick && onRowClick(row);
        }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [rows, cursor, onRowClick]);

  // Keep the cursored row visible (it sits below the sticky header/toolbar).
  useEffect(() => {
    if (cursor < 0 || !tbodyRef.current) return;
    tbodyRef.current.children[cursor]?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  return (
    <table className={`ledger ledger--${density}`}>
      <thead>
        <tr>
          {columns.map((col) => (
            <th key={col.key} className={col.className}>
              {col.sortKey && onSortChange ? (
                <SortHead
                  label={col.label}
                  sortKey={col.sortKey}
                  activeKey={sortKey}
                  dir={sortBy}
                  onSort={handleHeadSort}
                />
              ) : (
                col.label
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody ref={tbodyRef}>
        {rows.map((row, i) => {
          const isActive = activeId != null && row.id === activeId;
          const cls =
            [
              row.status ? `s-${row.status}` : '',
              isActive ? 'is-active' : '',
              i === cursor ? 'is-cursor' : '',
            ]
              .filter(Boolean)
              .join(' ') || undefined;
          return (
            <tr
              key={row.id}
              className={cls}
              aria-selected={isActive || undefined}
              onClick={() => {
                setCursor(i);
                row.onClick ? row.onClick(row) : onRowClick && onRowClick(row);
              }}
            >
              {columns.map((col) => (
                <td key={col.key} className={col.className}>
                  {row.cells[col.key]}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default Ledger;
